// Unified in-memory aggregator for impressions captured during a screen view
// Separates company impressions from promotion impressions while sharing lifecycle

// COMPANY IMPRESSIONS
import type { SeenCompanyImpression, SeenPromotionImpression } from "@ecountabl/lib"

const KEY_SEPARATOR = ':$:&:'
// used to decide whether to track reported impressions or not
const LIMIT_ONE_VIEW_PER_SESSION = false

abstract class Aggregator {
  protected newEntries: Set<string>
  protected reportedEntries: Set<string> | null

  constructor() {
    this.newEntries = new Set<string>()
    this.reportedEntries = LIMIT_ONE_VIEW_PER_SESSION ? new Set<string>() : null
  }

  abstract impressionFromKey(key: string): SeenCompanyImpression | SeenPromotionImpression
  abstract buildKey(item: SeenCompanyImpression | SeenPromotionImpression): string
  abstract add(item: SeenCompanyImpression | SeenPromotionImpression): void
  abstract getAndClear(): SeenCompanyImpression[] | SeenPromotionImpression[]
  abstract resetAll(): void
}

class CompanyAggregator extends Aggregator {
  constructor() {
    super()
  }

  impressionFromKey(key: string): SeenCompanyImpression {
    const [source, companyId, companyName, index] = key.split(KEY_SEPARATOR)
    return {
      source,
      companyId,
      companyName,
      index: parseInt(index)
    }
  }

  buildKey(item: SeenCompanyImpression): string {
    const source = item.source
    const companyId = item.companyId
    const companyName = item.companyName || ''
    const index = (typeof item.index === 'number') ? String(item.index) : ''
    return [source, companyId, companyName, index].join(KEY_SEPARATOR)
  }

  add(item: SeenCompanyImpression): void {
    if (!item || !item.companyId || !item.source) return
    const key = this.buildKey(item)
    if (this.reportedEntries?.has(key)) return
    if (!this.newEntries.has(key)) {
      this.newEntries.add(key)
    }
  }

  getAndClear(): SeenCompanyImpression[] {
    const output = Array.from(this.newEntries.values())
    for (const key of output) this.reportedEntries?.add(key)
    this.newEntries.clear()
    return output.map(impression => this.impressionFromKey(impression))
  }

  resetAll(): void {
    this.newEntries.clear()
    this.reportedEntries?.clear()
  }
}

class PromotionAggregator extends Aggregator {
  constructor() {
    super()
  }

  impressionFromKey(key: string): SeenPromotionImpression {
    const [name, index, url, customerId] = key.split(KEY_SEPARATOR)
    return {
      name,
      index: parseInt(index),
      url,
      customerId
    }
  }

  buildKey(item: SeenPromotionImpression): string {
    const name = item.name || ''
    const index = (typeof item.index === 'number') ? String(item.index) : ''
    const url = item.url || ''
    const customerId = item.customerId || ''
    return [name, index, url, customerId].join(KEY_SEPARATOR)
  }

  add(item: SeenPromotionImpression): void {
    if (!item || !item.name || item.index === undefined || Number.isNaN(item.index)) return
    const key = this.buildKey(item)
    if (this.reportedEntries?.has(key)) return
    if (!this.newEntries.has(key)) {
      this.newEntries.add(key)
    }
  }

  getAndClear(): SeenPromotionImpression[] {
    const newImpressions = Array.from(this.newEntries.values())
    for (const key of newImpressions) this.reportedEntries?.add(key)
    this.newEntries.clear()
    return newImpressions.map(impression => this.impressionFromKey(impression))
  }

  resetAll(): void {
    this.newEntries.clear()
    this.reportedEntries?.clear()
  }
}

class ImpressionAggregator {
  company: CompanyAggregator
  promotion: PromotionAggregator

  constructor() {
    this.company = new CompanyAggregator()
    this.promotion = new PromotionAggregator()
  }

  resetAll(): void {
    this.company.resetAll()
    this.promotion.resetAll()
  }
}

const impressionAggregator = new ImpressionAggregator()

export default impressionAggregator


